import React, { useState } from 'react';
import { RotateCcw, ArrowLeft } from 'lucide-react';
import type { PayMethod, PayMode, PosCustomer, PosLine, SaleTotals } from './types';
import { computeSaleTotals, formatUzs, today } from './saleTotals';

export interface ConfirmedSale {
  method: PayMethod;
  totals: SaleTotals;
  dueDate: string | null;
}

interface Props {
  lines: PosLine[];
  customer: PosCustomer;
  canEditPrices: boolean;
  customPrices: Record<number, number>;
  priceErrors: Record<number, string>;
  onPriceChange: (productId: number, raw: string) => void;
  onConfirm: (sale: ConfirmedSale) => void;
  submitting: boolean;
  onBack: () => void;
}

/**
 * To'lov ekrani — narx tahriri, to'lov usuli va qarz muddati.
 *
 * Tasdiqlash tugmasi ikki holatda bloklanadi: narxda xato bo'lsa va qarz
 * qolayotgan bo'lsa-yu muddat ko'rsatilmagan bo'lsa. Ikkinchisi muhim —
 * muddatsiz qarzlar hech qachon undirilmay to'planib qolardi.
 */
export const PaymentPanel: React.FC<Props> = ({
  lines,
  customer,
  canEditPrices,
  customPrices,
  priceErrors,
  onPriceChange,
  onConfirm,
  submitting,
  onBack,
}) => {
  const [method, setMethod] = useState<PayMethod>('cash');
  const [payMode, setPayMode] = useState<PayMode>('full');
  const [payAmountInput, setPayAmountInput] = useState('');
  const [dueDate, setDueDate] = useState('');

  const subtotal = lines.reduce((sum, line) => sum + line.subtotal, 0);
  const totals = computeSaleTotals({
    subtotal,
    oldDebt: customer.debt,
    payMode,
    payAmountInput,
  });

  const minDate = today();
  const hasPriceErrors = Object.keys(priceErrors).length > 0;
  // O'tmishdagi sanani ham to'xtatamiz: yilni "26" deb terib yuborilsa
  // sana 0026-yilga tushib qolardi.
  const dueDateInvalid = Boolean(dueDate) && dueDate < minDate;
  const needsDueDate = totals.remaining > 0 && (!dueDate || dueDateInvalid);
  const blocked = hasPriceErrors || needsDueDate || submitting;

  const confirmLabel = hasPriceErrors
    ? 'Narxni to\'g\'rilang'
    : needsDueDate
      ? (dueDateInvalid ? 'Muddat o\'tmishda' : 'Qarz muddatini tanlang')
      : submitting
        ? 'Saqlanmoqda…'
        : `Tasdiqlash — ${formatUzs(totals.paying)}`;

  return (
    <div className="space-y-4">
      <div className="page-bar flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="w-10 h-10 shrink-0 grid place-items-center rounded-lg text-gray-500 hover:bg-gray-100"
          aria-label="Orqaga"
        >
          <ArrowLeft size={20} />
        </button>
        <span className="font-semibold text-gray-900">To'lov</span>
      </div>

      {/* Mijoz */}
      <div className="card p-4">
        <div className="text-sm text-gray-500">Mijoz</div>
        <div className="font-semibold text-gray-900">{customer.name}</div>
        <div className="text-sm text-gray-500">{customer.phone}</div>
        {totals.oldDebt > 0 && (
          <div className="text-sm text-danger-600 mt-2 tabular-nums">
            Eski qarz: {formatUzs(totals.oldDebt)}
          </div>
        )}
      </div>

      {/* Tovarlar */}
      <div className="card p-4">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-sm text-gray-500">Tovarlar ({lines.length})</div>
          {canEditPrices && (
            <div className="text-xs text-primary-800">Narxni pastroq qo'ya olasiz</div>
          )}
        </div>

        <div className="divide-y divide-gray-100">
          {lines.map((line) => {
            const error = priceErrors[line.product.id];
            const custom = customPrices[line.product.id];

            return (
              <div key={line.product.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{line.product.name}</div>
                    <div className="text-sm text-gray-500 mt-0.5 tabular-nums">
                      {line.quantity} ×{' '}
                      {line.isDiscounted && (
                        <span className="line-through text-gray-400 mr-1">
                          {line.product.sell_price_uzs.toLocaleString('uz-UZ')}
                        </span>
                      )}
                      <span className={line.isDiscounted ? 'text-danger-600 font-medium' : ''}>
                        {line.price.toLocaleString('uz-UZ')} so'm
                      </span>
                    </div>
                  </div>
                  <div className="font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                    {formatUzs(line.subtotal)}
                  </div>
                </div>

                {canEditPrices && (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      className={`input flex-1 ${error ? 'border-danger-600' : ''}`}
                      placeholder={`Asl: ${line.product.sell_price_uzs.toLocaleString('uz-UZ')}`}
                      value={custom ?? ''}
                      onChange={(e) => onPriceChange(line.product.id, e.target.value)}
                      aria-label={`${line.product.name} narxi`}
                    />
                    {custom != null && (
                      <button
                        type="button"
                        onClick={() => onPriceChange(line.product.id, '')}
                        className="btn btn-secondary shrink-0"
                        aria-label="Asl narxga qaytarish"
                      >
                        <RotateCcw size={16} />
                      </button>
                    )}
                  </div>
                )}
                {error && <div className="text-xs text-danger-600 mt-1">{error}</div>}
                {canEditPrices && custom != null && !error && line.product.cost_uzs != null && (
                  <div className="text-xs text-gray-500 mt-1 tabular-nums">
                    Tan narx: {line.product.cost_uzs.toLocaleString('uz-UZ')} so'm
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>


      {/* Hisob */}
      <div className="card p-4 space-y-2 text-sm">
        <Row label="Buyurtma" value={formatUzs(totals.orderTotal)} />
        {totals.oldDebt > 0 && (
          <Row label="Eski qarz" value={formatUzs(totals.oldDebt)} tone="danger" />
        )}
        <div className="flex items-baseline justify-between pt-2 mt-1 border-t border-gray-100">
          <span className="font-semibold text-gray-900">Jami</span>
          <span className="text-xl font-bold text-gray-900 tabular-nums">
            {formatUzs(totals.grandTotal)}
          </span>
        </div>
      </div>

      {/* To'lov usuli */}
      <div className="card p-4">
        <div className="label">To'lov usuli</div>
        <div className="grid grid-cols-2 gap-2">
          <Choice active={method === 'cash'} onClick={() => setMethod('cash')}>Naqd</Choice>
          <Choice active={method === 'card'} onClick={() => setMethod('card')}>Karta</Choice>
        </div>

        <div className="label mt-4">Qancha to'laydi</div>
        <div className="grid grid-cols-3 gap-2">
          <Choice
            active={payMode === 'full'}
            onClick={() => { setPayMode('full'); setPayAmountInput(''); }}
          >
            To'liq
          </Choice>
          <Choice active={payMode === 'partial'} onClick={() => setPayMode('partial')}>Qisman</Choice>
          <Choice active={payMode === 'credit'} onClick={() => setPayMode('credit')}>Qarzga</Choice>
        </div>

        {payMode === 'partial' && (
          <div className="mt-3">
            <label className="label" htmlFor="pos-pay-amount">Olingan summa</label>
            <input
              id="pos-pay-amount"
              className="input"
              type="number"
              min={0}
              placeholder="0"
              value={payAmountInput}
              onChange={(e) => setPayAmountInput(e.target.value)}
              autoFocus
            />
          </div>
        )}

        {totals.remaining > 0 && (
          <div className="mt-3">
            <label className="label" htmlFor="pos-due-date">Qarz muddati</label>
            <input
              id="pos-due-date"
              className="input"
              type="date"
              min={minDate}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            {dueDateInvalid && (
              <p className="text-xs text-danger-600 mt-1">Muddat o'tmishda bo'lishi mumkin emas.</p>
            )}
            {!dueDate && (
              <p className="text-xs text-gray-500 mt-1">
                Qarz qolyapti — qachon undirilishini belgilang.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Natija */}
      <div className="card p-4 space-y-2 text-sm bg-primary-50 border-primary-200">
        <Row label="To'lanadi" value={formatUzs(totals.paying)} tone="brand" strong />
        <Row
          label="Qarz qoladi"
          value={formatUzs(totals.remaining)}
          tone={totals.remaining > 0 ? 'danger' : 'brand'}
          strong
        />
      </div>

      <button
        type="button"
        className="btn btn-primary w-full"
        disabled={blocked}
        onClick={() =>
          onConfirm({
            method,
            totals,
            dueDate: totals.remaining > 0 ? dueDate || null : null,
          })
        }
      >
        {confirmLabel}
      </button>
    </div>
  );
};

const Row: React.FC<{
  label: string;
  value: string;
  tone?: 'brand' | 'danger';
  strong?: boolean;
}> = ({ label, value, tone, strong }) => (
  <div className="flex items-baseline justify-between">
    <span className="text-gray-600">{label}</span>
    <span
      className={`tabular-nums ${strong ? 'font-bold' : 'font-medium'} ${
        tone === 'brand' ? 'text-primary-800' : tone === 'danger' ? 'text-danger-600' : 'text-gray-900'
      }`}
    >
      {value}
    </span>
  </div>
);

const Choice: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`py-2.5 rounded-lg border text-sm font-medium transition-colors ${
      active
        ? 'border-primary-600 bg-primary-600 text-white'
        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
    }`}
  >
    {children}
  </button>
);
